import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Truck, ShieldCheck, XCircle } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [valid, setValid] = useState(null); // null=loading, true, false
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/reset-password/${token}`)
      .then((r) => { setValid(true); setEmail(r.data.email); setName(r.data.name); })
      .catch((e) => { setValid(false); setErr(formatApiError(e)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("Password must be at least 6 characters");
    if (pw !== pw2) return setErr("Passwords do not match");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-[var(--navy)]">
        <img src="https://images.unsplash.com/photo-1726866672851-5b99c837603c?crop=entropy&cs=srgb&fm=jpg&q=85" className="absolute inset-0 w-full h-full object-cover opacity-30" alt="logistics" />
        <div className="relative z-10 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2"><Truck size={22} className="text-amber-400" /><div className="font-heading font-extrabold text-xl">SRAD Dispatch Hub</div></div>
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight">Set a new<br /><span className="text-amber-400">password.</span></h1>
            <p className="text-sm text-slate-300 mt-4 max-w-md">Choose something strong you'll remember. This device won't be signed in automatically — you'll return to the sign-in page.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm" data-testid="reset-page">
          {valid === null && <div className="text-sm text-slate-500">Verifying link…</div>}

          {valid === false && (
            <div className="bg-rose-50 border border-rose-200 rounded-md p-5 text-sm text-rose-800" data-testid="reset-invalid">
              <div className="flex items-center gap-2 font-semibold"><XCircle size={16} /> Link invalid</div>
              <div className="text-xs mt-2">{err || "This reset link is invalid, has been used, or expired."}</div>
              <div className="flex gap-2 mt-4">
                <Link to="/forgot-password" className="text-sm bg-[var(--navy)] text-white rounded-md px-3 py-1.5 font-semibold">Request new link</Link>
                <Link to="/login" className="text-sm border border-slate-300 rounded-md px-3 py-1.5 font-semibold">Sign in</Link>
              </div>
            </div>
          )}

          {valid === true && !done && (
            <form onSubmit={submit}>
              <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Password Reset</div>
                <h2 className="font-heading text-3xl font-extrabold text-slate-900">Set new password</h2>
                <p className="text-sm text-slate-500 mt-2">For <b>{name || email}</b></p>
              </div>
              {err && <div data-testid="reset-error" className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">New Password</label>
              <input data-testid="reset-pw" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full mt-1 mb-3 px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Confirm Password</label>
              <input data-testid="reset-pw2" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full mt-1 mb-6 px-3 py-2.5 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
              <button data-testid="reset-submit" disabled={loading} className="w-full bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold py-2.5 rounded-md disabled:opacity-60">
                {loading ? "Updating…" : "Set New Password"}
              </button>
            </form>
          )}

          {done && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-5 text-sm text-emerald-800" data-testid="reset-success">
              <div className="flex items-center gap-2 font-semibold"><ShieldCheck size={16} /> Password updated</div>
              <div className="text-xs mt-2">Redirecting you to sign in…</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
